import {Controller} from "@hotwired/stimulus"

const MAX_ROWS = 10
const UNCOMMITTED_MESSAGE = "Kies eerst een kind uit de lijst. De debiteuren en producten horen bij het gekozen kind."

// The endpoint renders its JSON through ERB, so names arrive HTML-escaped.
function decode(text) {
  return new DOMParser().parseFromString(text || "", "text/html").documentElement.textContent
}

/**
 * Child search for billing items. Only a click or Enter chooses a row: hovering never writes the id,
 * so it cannot trigger the form reload that listens for `billing-source-search:chosen`.
 *
 * With `guardSubmit`, the form refuses to submit until the chosen child is the one the page was
 * rendered for, because the debiteur and product selects belong to that child.
 */
export default class extends Controller {
  static targets = ["input", "id", "list"]
  static values = {
    url: String,
    minChars: {type: Number, default: 1},
    delay: {type: Number, default: 300},
    guardSubmit: Boolean,
  }

  connect() {
    this.results = []
    this.active = -1
    this.committedId = this.idTarget.value
    this.form = this.inputTarget.form
    this.boundGuard = this.guard.bind(this)
    if (this.guardSubmitValue && this.form) this.form.addEventListener("submit", this.boundGuard)
  }

  disconnect() {
    this.cancel()
    if (this.form) this.form.removeEventListener("submit", this.boundGuard)
  }

  search() {
    this.cancel()
    this.idTarget.value = ""
    this.inputTarget.setCustomValidity("")
    this.close()

    const query = this.inputTarget.value.trim()
    if (query.length < this.minCharsValue) return

    this.timer = setTimeout(() => this.load(query), this.delayValue)
  }

  async load(query) {
    const request = new AbortController()
    this.request = request

    try {
      const response = await fetch(`${this.urlValue}?query=${encodeURIComponent(query)}`, {
        headers: {Accept: "application/json"},
        credentials: "same-origin",
        signal: request.signal,
      })
      if (!response.ok) return

      const items = await response.json()
      if (request.signal.aborted) return
      this.render(items)
    } catch (error) {
      if (error.name !== "AbortError") console.error(error)
    }
  }

  render(items) {
    this.results = items.filter((item) => item.object_id).slice(0, MAX_ROWS)
    this.active = -1
    this.listTarget.replaceChildren(...this.results.map((item, index) => this.row(item, index)))
    this.listTarget.style.display = this.results.length > 0 ? "block" : "none"
  }

  row(item, index) {
    const organisation = document.createElement("span")
    organisation.className = "organisation"
    organisation.textContent = decode(item.organisation)

    const label = document.createElement("div")
    label.className = "eac-item"
    label.textContent = decode(item.name)
    label.append(organisation)

    const li = document.createElement("li")
    li.dataset.index = index
    li.dataset.cy = "billing-source-search-result"
    li.dataset.action = "mousedown->billing-source-search#choose"
    li.append(label)
    return li
  }

  navigate(event) {
    if (event.isComposing || !this.isOpen) return

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const step = event.key === "ArrowDown" ? 1 : -1
      this.active = (this.active + step + this.results.length) % this.results.length
      this.rows.forEach((li, index) => li.classList.toggle("selected", index === this.active))
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.select(Math.max(this.active, 0))
    } else if (event.key === "Escape") {
      this.close()
    }
  }

  // Mousedown, not click: the input's blur closes the list before a click would land.
  choose(event) {
    event.preventDefault()
    this.select(Number(event.currentTarget.dataset.index))
  }

  select(index) {
    const item = this.results[index]
    this.inputTarget.value = decode(item.name)
    this.idTarget.value = item.object_id
    this.inputTarget.setCustomValidity("")
    this.close()
    this.dispatch("chosen", {target: this.inputTarget, detail: {id: item.object_id}})
  }

  close() {
    this.results = []
    this.active = -1
    this.listTarget.replaceChildren()
    this.listTarget.style.display = "none"
  }

  cancel() {
    clearTimeout(this.timer)
    if (this.request) this.request.abort()
  }

  // Stops propagation too: jquery-ujs disables the submit button from a document handler and never
  // re-enables it when the submission is cancelled.
  guard(event) {
    const chosenId = this.idTarget.value
    if (chosenId !== "" && chosenId === this.committedId) return

    event.preventDefault()
    event.stopPropagation()
    this.inputTarget.setCustomValidity(UNCOMMITTED_MESSAGE)
    this.inputTarget.reportValidity()
  }

  get isOpen() {
    return this.results.length > 0
  }

  get rows() {
    return Array.from(this.listTarget.children)
  }
}
